import {
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { HiShieldCheck } from "react-icons/hi";
import { RiImageEditFill } from "react-icons/ri";
import { IMaskInput } from "react-imask";
import { useSelector } from "react-redux";
import { UserAPI } from "../../api/UserAPI";
import { formatRoleLabel } from "../../util/UserRoles";
import { AuthContext } from "../auth/AuthContext";
import Loader from "../../component/Loader";
import PopUpAlert from '../../component/PopUpAlert'
import defaultDP from '../../assets/images/avatar-design.png';
import CustomPlaceAutocomplete from "../CustomComponents/CustomPlaceAutocomplete";

// ✅ CSS imports
import styles from '../../styles/modules/Profile.module.css';

const Profile = () => {
  const userInfo = useSelector((store) => store.user.userInfo);
  const selectedClub = useSelector((store) => store.user.selectedClub);
  const { getCurrentUser } = useContext(AuthContext);
  const [message, setMessage] = useState({ type: '', content: '' })
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState({ day: '', month: '', year: '' });
  const [image, setImage] = useState("");
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    address: false,
    phone: false,
    countryCode: false,
  });
  const [imagePre, setImagePre] = useState('');

  Object.keys(formData).length === 0 && setFormData({
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    countryCode: "+1",
    email: userInfo.email,
    address: userInfo.address,
    phone: userInfo.phone
  });

  useEffect(() => {
    setIsLoading(true);
    if (userInfo?.userId) {
      setImagePre(
        userInfo?.profileImage
          ?
          import.meta.env.VITE_APP_BASE_URL + '/user/user-profile-img/' + userInfo?.profileImage
          :
          defaultDP
      );
      const dateArray = userInfo?.dob?.split('-')

      setDateOfBirth({
        day: dateArray?.[2] ? Number(dateArray[2]) : '',
        year: dateArray?.[0] || '',
        month: dateArray?.[1] ? Number(dateArray[1]) - 1 : '',
      })
      setIsLoading(false);
      setTimeout(() => {
        setMessage({ type: '', content: "" });
      }, 4000);
    }
  }, [userInfo])

  const handleFormData = (event) => {
    let newVal;
    const { name, value } = event.target;
    if (name === 'phone') {
      if (!value.startsWith('1')) {
        newVal = '1' + value;
      } else {
        newVal = value;
      }
    }
    else {
      newVal = value;
    }
    setFormData({ ...formData, [name]: newVal });
    Object.keys(errors).length != 0 && setErrors({});
  };

  const handleDOBData = (event) => {
    const { name, value } = event.target;
    setDateOfBirth({ ...dateOfBirth, [name]: value });
  };

  const handleImageUpload = (event) => {
    setMessage({ type: '', content: "" });
    const file = event.target.files[0];
    const maxSizeInMB = 5;
    if (file.type === "image/webp" || file.type === "image/jpg" || file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/gif") {

      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > maxSizeInMB) {
        setMessage({ type: 'info', content: "upload image under 5 MB" });
      }
      else {
        setImage(file);
        setImagePre(URL.createObjectURL(file));
      }
    } else {
      setMessage({ type: 'warning', content: "upload .jpg, .jpeg, .png, .gif,.webp file" });
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = Array.from(
    { length: new Date().getFullYear() - 1900 + 1 },
    (_, i) => 1900 + i
  );

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const maxDays = getDaysInMonth(dateOfBirth.year || new Date().getFullYear(), dateOfBirth.month || 0);
  const days = Array.from({ length: maxDays }, (_, i) => i + 1);

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 200,
      },
    },
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage({ type: '', content: "" });
    let errorFields = {};
    if (!formData.firstName?.trim()) {
      errorFields.firstName = true;
    }
    if (!formData.lastName?.trim()) {
      errorFields.lastName = true;
    }
    if (!formData.address?.trim()) {
      errorFields.address = true;
    }
    setErrors(errorFields);

    if (
      !formData.firstName?.trim() ||
      !formData.lastName?.trim() ||
      !formData.address?.trim()
    ) {
      return;
    }

    let dob;
    let formattedDate;
    if (!dateOfBirth.month && !dateOfBirth.year && !dateOfBirth.day) {
      dob = new Date(years[0], 0, 1);
    } else {
      const maxDays = getDaysInMonth(dateOfBirth.year, dateOfBirth.month);
      const day = Math.min(dateOfBirth.day, maxDays);

      dob = new Date(dateOfBirth.year, dateOfBirth.month, day);
      const year = dob.getFullYear();
      const month = String(dob.getMonth() + 1).padStart(2, "0");
      const d = String(dob.getDate()).padStart(2, "0");
      formattedDate = `${year}-${month}-${d}`;
    }
    try {
      const userUpdateData = {
        userFirstName: formData.firstName,
        userLastName: formData.lastName,
        userPhone: formData.phone.startsWith('+') ? formData.phone : "+" + formData.phone,
        userDOB: formattedDate,
        userAddress: formData.address
      }

      setIsLoading(true);
      await UserAPI.updateUser(userUpdateData, userInfo.userId, selectedClub.clubId, image);
      getCurrentUser();
      setIsLoading(false);
      setMessage({ type: 'success', content: "Profile updated successfully" });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setMessage({ type: 'error', content: "Please contact customer support" });
    }
  };

  const getRoleNames = useCallback(() => {
    if (!userInfo?.authorities) return [];

    return userInfo.authorities
      .map(role => formatRoleLabel(role.roleName))
      .filter(Boolean);
  }, [userInfo]);

  return (
    <form className="user-profile-container" onSubmit={handleSubmit}>
      {isLoading && <Loader />}
      <PopUpAlert type={message.type} message={message.content} />

      <div className="user-profile-image-section">
        <div className="user-profile-image">
          <label htmlFor="file_upload" className="profile_image_edit">
            <RiImageEditFill className="profile_image_edit_icon" />
          </label>
          <label className="profile_image_preview">
            <img
              loading="lazy"
              src={imagePre}
              alt=""
            />
          </label>
          <input
            type="file"
            id="file_upload"
            name="image"
            onChange={handleImageUpload}
          />
        </div>
        <div className="user-profile-heading">
          <div className="user-profile-heading-content">
            <h1>My Profile</h1>
            <div className="user-profile-heading-content-roles">
              {getRoleNames()?.map((data, index) =>
                <div className="user_profile_role_tag" key={index}>{data}</div>
              )}
            </div>
          </div>
          <p><HiShieldCheck className="user-account-status" /> Account verified</p>
        </div>
      </div>

      <div className="user-profile-name-section">
        <TextField
          className="user-profile-text-input"
          sx={{
            width: "48%",
            "@media (max-width:600px)": {
              width: "100%",
            },
          }}
          inputProps={{ maxLength: 100 }}
          size="small"
          variant="standard"
          label="First Name"
          name="firstName"
          value={formData.firstName || ""}
          onChange={handleFormData}
          error={errors.firstName}
          helperText={errors.firstName && "First name is required"}
        />

        <TextField
          className="user-profile-text-input"
          sx={{
            width: "48%",
            "@media (max-width:600px)": {
              width: "100%",
            },
          }}
          size="small"
          inputProps={{ maxLength: 100 }}
          variant="standard"
          label="Last Name"
          name="lastName"
          value={formData.lastName || ""}
          onChange={handleFormData}
          error={errors.lastName}
          helperText={errors.lastName && "Last name is required"}
        />
      </div>

      <div className="user-profile-date-mobile-section">
        <div className="user-date-of-birth">
          <h4>Date of Birth</h4>
          <div className="user-date-of-birth-body">
            <div className="user-date-of-birth-day">
              <p>Day</p>
              <FormControl
                className="user-profile-select-input"
                sx={{
                  minWidth: 0,
                  width: '100%',
                }}
                size="small"
              >
                <Select
                  sx={{ borderRadius: 10 }}
                  labelId="demo-select-small-label"
                  name="day"
                  value={dateOfBirth.day || days[0] || ''}
                  onChange={handleDOBData}
                  id="demo-select-small"
                  MenuProps={menuProps}
                >
                  {days.map((item, index) => (
                    <MenuItem key={index} value={item}>{item} </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="user-date-of-birth-day">
              <p>Month</p>
              <FormControl
                className="user-profile-select-input"
                sx={{
                  minWidth: 120,
                  width: '100%',
                }}
                size="small"
              >
                <Select
                  defaultValue={0}
                  sx={{ borderRadius: 10 }}
                  labelId="demo-select-small-label"
                  id="demo-select-small"
                  name="month"
                  value={dateOfBirth.month || 0}
                  onChange={handleDOBData}
                  MenuProps={menuProps}
                >
                  {months.map((item, index) => (
                    <MenuItem key={index} value={index}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="user-date-of-birth-day">
              <p>Year</p>
              <FormControl
                className="user-profile-select-input"
                sx={{
                  minWidth: 120,
                  width: '100%',
                }}
                size="small"
              >
                <Select
                  sx={{ borderRadius: 10 }}
                  labelId="demo-select-small-label"
                  id="demo-select-small"
                  MenuProps={menuProps}
                  name="year"
                  value={dateOfBirth.year || years[0] || ''}
                  onChange={handleDOBData}
                >
                  {years.map((item, index) => (
                    <MenuItem key={index} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </div>

        <div className="user-mobile-number-input">
          <TextField
            disabled
            className="user-profile-text-input"
            sx={{ width: "100%" }}
            label="Phone"
            variant="standard"
            name="phone"
            value={formData.phone || ""}
          />
        </div>
      </div>

      <div className="user-profile-email-section">
        <TextField
          disabled
          className="user-profile-text-input"
          sx={{
            width: "100%",
          }}
          label="Email"
          variant="standard"
          name="email"
          inputProps={{ maxLength: 100 }}
          value={formData.email || ""}
        />
      </div>

      <div className="user-profile-address-section">
        <h4>Residence Address</h4>
        <div className="user-profile-address-section-body">
          <div className="user-profile-address-section-part-1">
            <CustomPlaceAutocomplete
              onChange={(val) => {
                handleFormData({ target: { name: 'address', value: val } })
              }}
              error={errors.address}
              helperText={errors.address && "Address is required"}
              value={formData?.address || ''}
              label="Address" />
          </div>
        </div>
      </div>

      <div className="user-profile-save-btn">
        <Button type="submit" variant="contained">
          Save
        </Button>
      </div>
    </form>
  );
};

export default Profile;